import React, { useEffect, useRef, useState } from 'react'
import { Accordion } from 'react-bootstrap'

import { selectContact, selectContactChange, selectIsLoadingContactChange, selectSelectedKeys, setSelectedKeys } from '../../store/meetingSummary/contactUpdate'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { ExternalContactUpdateViewProps, UpToDate } from './UpdateExternalContactModal'
import { UpdateExternalContactFormFooterComponent } from './UpdateExternalContactForm'
import { gatherAllChildSuccessfulFields } from './utils'
import { SegmentedExternalContactUpdateViewElement } from './SegmentedExternalContactUpdateViewElement'
import { useGetContactSchemaQuery } from '../../store/integrations/integrations'
import { selectAccountId } from '../../store/auth/authSlice'

export const SegmentedExternalContactUpdateView: React.FC<ExternalContactUpdateViewProps> = ({ serviceId, onKeySelected }) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const contact = useAppSelector(selectContact)
  const isLoadingContactChange = useAppSelector(selectIsLoadingContactChange)
  const contactChange = useAppSelector(selectContactChange)
  const selectedKeys = useAppSelector(selectSelectedKeys)

  const [activeKey, setActiveKey] = useState<string>()
  const [contactSchemaToShow, setContactSchemaToShow] = useState<{ [key: string]: any } | null>(null)
  const { data: contactSchemaResponse } = useGetContactSchemaQuery({ accountId, connector: serviceId, context: { objectName: contact?.type ?? 'contact' } })

  const externalInfo = contact?.externalInfos.find((e) => e.serviceId === serviceId)

  useEffect(() => {
    if (!contactSchemaResponse) {
      return
    }

    if (externalInfo) {
      setContactSchemaToShow(externalInfo.contactSchema)
      return
    }
    setContactSchemaToShow(contactSchemaResponse.data)
  }, [externalInfo, contactSchemaResponse])

  const hasUncheckedSuccessfulFields = useRef(false)

  useEffect(() => {
    if (!contactChange || !contactSchemaToShow || !selectedKeys || !selectedKeys.length || hasUncheckedSuccessfulFields.current) {
      return
    }
    const { contactValues, metadata } = contactChange

    if (externalInfo) {
      const fieldsToDeselect = Object.entries(externalInfo.contactSchema).reduce((acc: string[], [key, panel]): string[] => {
        return [...acc, ...gatherAllChildSuccessfulFields(contactValues[key], key, panel, metadata, Object.keys(contactValues[key] ?? {}), 'contactValues')]
      }, [])
      dispatch(setSelectedKeys(selectedKeys.filter((key) => !fieldsToDeselect.includes(key))))
    }

    hasUncheckedSuccessfulFields.current = true
  }, [contactChange, contactSchemaToShow, externalInfo, selectedKeys, dispatch])

  const loadingMessages = [
    'Loading...',
    'Retrieving client data from CRM and planning software…',
    'Analyzing recent meeting notes with AI…',
    'Generating updated contact details from conversation insights…',
    'Reviewing data for consistency and accuracy…',
    'Finalizing contact updates for advisor review…',
  ]
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0)

  useEffect(() => {
    if (!isLoadingContactChange) return

    const interval = setInterval(() => {
      setLoadingMessageIndex((prevIndex) => (prevIndex + 1) % loadingMessages.length)
    }, 15000)

    return () => {
      clearInterval(interval)
    }
  }, [isLoadingContactChange, loadingMessages.length])

  if (isLoadingContactChange || !contactSchemaToShow) {
    return (
      <div className='skeleton contact-information-skeleton d-flex align-items-center justify-content-center w-100'>{loadingMessages[loadingMessageIndex]}</div>
    )
  }

  if (!contactChange) {
    return <UpToDate serviceId={serviceId} />
  }

  const { contactSchema, contactValues, previousContactValues, metadata = {} } = contactChange

  return (
    <>
      <Accordion activeKey={activeKey} onSelect={(e) => setActiveKey(`${e}`)}>
        {Object.entries(contactSchemaToShow).map(([key, panel]) => {
          return (
            <SegmentedExternalContactUpdateViewElement
              serviceId={serviceId}
              panel={panel}
              metadata={metadata}
              contactSchema={contactSchema}
              contactValues={contactValues}
              key={key}
              panelKey={key}
              previousContactValues={previousContactValues}
              onKeySelected={onKeySelected}
            />
          )
        })}
      </Accordion>
      <UpdateExternalContactFormFooterComponent />
    </>
  )
}
